import { z } from 'zod';
/**
 * Shift patterns and their materialisation (slice-03 §4, §5, exit criterion 2).
 *
 * A pattern is written in **local** wall-clock terms — weekdays, `HH:MM` start
 * and end, dates — because that is what a recurrence is: "Tuesdays at ten in
 * the client's home", whatever the offset that week. The zone is the service
 * location's, resolved by the server and returned alongside, so the pair is
 * never a naked local time on the wire. Visits it produces cross as instants.
 *
 * ## Editing offers three scopes
 *
 *  - **This occurrence** — edit that visit: `PATCH /v1/visits/:id`.
 *  - **This and future** — the pattern splits at the chosen occurrence.
 *  - **All future** — the pattern splits at today.
 *
 * Past visits are unchanged whichever is chosen. A split ends the old pattern
 * the day before and starts a new one that names it in `supersedesId`.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-06 — four digits, two, two.');
const clockTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time like 09:00 or 17:30.');
/** 0 = Sunday … 6 = Saturday — the database's `days_of_week`. */
const daysOfWeek = z
    .array(z.number().int().min(0).max(6))
    .min(1, 'Choose at least one day.')
    .refine((days) => new Set(days).size === days.length, { message: 'Each day once.' });
export const shiftPatternStatusSchema = z.enum(['ACTIVE', 'PAUSED', 'ENDED']);
export const createShiftPatternSchema = z
    .object({
    clientId: z.string().uuid(),
    /** Null: the pattern produces open shifts. */
    caregiverId: z.string().uuid().nullable(),
    serviceCodeId: z.string().uuid(),
    clientAddressId: z.string().uuid(),
    authorizationId: z.string().uuid().nullish(),
    daysOfWeek,
    startTime: clockTime,
    /** At or before `startTime`: the shift ends the next morning. */
    endTime: clockTime,
    weekInterval: z.number().int().min(1).max(4).default(1),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullish(),
})
    .refine((body) => body.startTime !== body.endTime, {
    path: ['endTime'],
    message: 'A shift needs a length. Pick an end time different from the start.',
})
    .refine((body) => !body.effectiveTo || body.effectiveTo >= body.effectiveFrom, {
    path: ['effectiveTo'],
    message: 'The last day can’t be before the first.',
});
/** What a "this and future" or "all future" edit may change. Absent means unchanged. */
export const shiftPatternChangesSchema = z
    .object({
    caregiverId: z.string().uuid().nullable().optional(),
    serviceCodeId: z.string().uuid().optional(),
    clientAddressId: z.string().uuid().optional(),
    authorizationId: z.string().uuid().nullable().optional(),
    daysOfWeek: daysOfWeek.optional(),
    startTime: clockTime.optional(),
    endTime: clockTime.optional(),
    weekInterval: z.number().int().min(1).max(4).optional(),
    effectiveTo: isoDate.nullable().optional(),
})
    .refine((changes) => Object.keys(changes).length > 0, {
    message: 'Change at least one thing.',
});
export const editShiftPatternSchema = z.object({
    scope: z.enum(['THIS_OCCURRENCE', 'THIS_AND_FUTURE', 'ALL_FUTURE']),
    /** The occurrence a "this and future" edit starts from. */
    occurrenceDate: isoDate.nullish(),
    changes: shiftPatternChangesSchema,
});
export const endShiftPatternSchema = z.object({
    /** The last day it runs. Today at the earliest; yesterday ends it now. */
    lastDate: isoDate,
    reason: z.string().trim().min(1, 'Say why the pattern is ending.').max(2_000),
});
export const shiftPatternListQuerySchema = z.object({
    clientId: z.string().uuid().optional(),
    caregiverId: z.string().uuid().optional(),
    /** The literal strings, so `false` cannot coerce to true. */
    includeEnded: z.enum(['true', 'false']).optional(),
});
export const shiftPatternSchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    clientName: z.string(),
    caregiverId: z.string().uuid().nullable(),
    caregiverName: z.string().nullable(),
    serviceCodeId: z.string().uuid(),
    serviceCode: z.string(),
    clientAddressId: z.string().uuid().nullable(),
    authorizationId: z.string().uuid().nullable(),
    /** The service location's zone; the times below are in it. */
    timezone: z.string(),
    daysOfWeek: z.array(z.number().int()),
    startTime: z.string(),
    endTime: z.string(),
    crossesMidnight: z.boolean(),
    weekInterval: z.number().int(),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullable(),
    materialisedThrough: isoDate.nullable(),
    status: shiftPatternStatusSchema,
    supersedesId: z.string().uuid().nullable(),
});
export const shiftPatternListSchema = z.object({ patterns: z.array(shiftPatternSchema) });
/**
 * What a materialisation run did. Every occurrence in the window is accounted
 * for: booked as the pattern says, or booked as an AT_RISK open shift with the
 * rules that refused the caregiver — never silently skipped, and never a run
 * that fails and leaves the board empty (RECOVERY §2, session 5).
 */
export const materialisationSchema = z.object({
    from: isoDate.nullable(),
    through: isoDate.nullable(),
    booked: z.number().int(),
    atRisk: z.array(z.object({
        date: isoDate,
        visitId: z.string().uuid(),
        reasons: z.array(z.object({ code: z.string(), message: z.string() })),
    })),
});
export const shiftPatternWriteResultSchema = z.object({
    pattern: shiftPatternSchema,
    /** Visits retired from the old pattern by a split or an end. */
    replacedVisits: z.number().int(),
    materialisation: materialisationSchema,
});
//# sourceMappingURL=shift-pattern.js.map